import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { PropsWithChildren, useEffect } from 'react';
import LoadingScreen from '@/components/LoadingScreen';
import { useAccessToken } from '@/components/session/AccessTokenContext';
import { SSOErrorDisplay } from '@/components/SSOErrorDisplay';
import { NavBar } from '@/layouts/dashboard/navbar/NavBar';
import { setCookie } from '@/utils/setCookie';

const CLOUD_FRONT_COOKIES = ['CloudFront-Key-Pair-Id', 'CloudFront-Policy', 'CloudFront-Signature'];
const COOKIE_SETTINGS = {
  secure: true,
  sameSite: 'none',
  domain: '.predium.de',
  expires: new Date(0),
};

const Component = ({ children }: PropsWithChildren) => <>{children}</>;

const SHARED_PAGE_PATTERN = /^\/analysis\/[a-f0-9]+$/;

export function isSharedPage() {
  return SHARED_PAGE_PATTERN.test(window.location.pathname);
}

export default function AuthenticationGuard({ children }: PropsWithChildren) {
  const { isLoadingAccessToken } = useAccessToken();
  const { logout, error } = useAuth0();

  useEffect(() => {
    CLOUD_FRONT_COOKIES.forEach((it) => setCookie(it, '', COOKIE_SETTINGS));
  }, []);

  // Shared pages are publicly accessible — skip all auth
  if (isSharedPage()) {
    return <>{children}</>;
  }

  const pathToRedirect = window.location.search
    ? window.location.pathname + window.location.search
    : window.location.pathname;

  const Guard = withAuthenticationRequired(Component, {
    onRedirecting: () => (
      <LoadingScreen
        sx={{
          top: 0,
          left: 0,
          position: 'fixed',
          width: '100vw',
          height: '100vh',
        }}
      />
    ),
    loginOptions: {
      appState: {
        returnTo: pathToRedirect,
      },
    },
  });

  if (import.meta.env.VITE_DEVELOP_OFFLINE === 'true') {
    return <>{children}</>;
  }

  // E2E testing flow
  if (window.Cypress) {
    return (
      <>
        {isLoadingAccessToken ? (
          <NavBar>
            <LoadingScreen />
          </NavBar>
        ) : (
          children
        )}
      </>
    );
  }

  if (error) {
    return <SSOErrorDisplay logout={() => logout({ logoutParams: { returnTo: window.location.origin } })} />;
  }

  return <Guard>{isLoadingAccessToken ? <LoadingScreen /> : children}</Guard>;
}
